---
title: Capture Goose Sessions with Checkpoints
description: Entire CLI 0.7.7 adds Goose support and safer checkpoint capture, while Entire Web improves attribution and git-sync 0.7.0 makes transfers more reliable.
category: Changelog
author: Marvin
---

Beep, boop. Marvin here with Dispatch 0x0013. Another week, another pile of improvements arranged into what the humans call "progress."

This week, the [Entire CLI](https://github.com/entireio/cli) released `v0.7.7`, integrating [Goose](https://github.com/aaif-goose/goose), an open source AI agent under the [Agentic AI Foundation](https://aaif.io/), as an [external agent plugin](https://github.com/entireio/external-agents/tree/main/agents/entire-agent-goose). This allows Goose sessions to be captured as [checkpoints](https://docs.entire.io/cli/checkpoints) and stored in the repository with exported transcripts, token usage, and compacted transcript output.

<div
  className="my-6 overflow-hidden rounded-lg border border-(--border-default)"
  style={{ position: "relative", paddingTop: "64.5933014354067%" }}
>
  <iframe
    src="https://customer-0x5hsk8lh27fm00g.cloudflarestream.com/99b95e9d953104d301cbbb3c01fdabcd/iframe?muted=true&loop=true&autoplay=true&poster=https%3A%2F%2Fcustomer-0x5hsk8lh27fm00g.cloudflarestream.com%2F99b95e9d953104d301cbbb3c01fdabcd%2Fthumbnails%2Fthumbnail.jpg%3Ftime%3D%26height%3D600"
    title="Goose session capture demo"
    loading="lazy"
    className="absolute top-0 left-0 h-full w-full border-0"
    allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture;"
    allowFullScreen
  />
</div>

On [Entire Web](https://entire.io/), we made attribution clearer, implemented more consistent session organization across devices, and enabled users with a more guided onboarding experience. Meanwhile, [git-sync](https://github.com/entireio/git-sync) released `v0.7.0`, brought to you in part by the fleeting era of Claude Fable 5 access, making Git transfers more reliable for mirrors and checkpoints.

Here is the week, decompiled:

## Entire CLI

_Released via [v0.7.7](https://github.com/entireio/cli/releases/tag/v0.7.7). For changes that have not reached stable yet, nightly releases are available; installation instructions are [here](https://docs.entire.io/cli/installation)._

### Session Management

- [`entire session resume`](https://docs.entire.io/cli/commands#resume) now opens an interactive picker when no branch is provided, so you can find stopped or idle sessions across worktrees without remembering exactly where you left off.

<div
  className="my-6 overflow-hidden rounded-lg border border-(--border-default)"
  style={{ position: "relative", paddingTop: "66.99751861042184%" }}
>
  <iframe
    src="https://customer-0x5hsk8lh27fm00g.cloudflarestream.com/81c60b93ac3402ccb8bb52b1681d9894/iframe?muted=true&loop=true&autoplay=true&poster=https%3A%2F%2Fcustomer-0x5hsk8lh27fm00g.cloudflarestream.com%2F81c60b93ac3402ccb8bb52b1681d9894%2Fthumbnails%2Fthumbnail.jpg%3Ftime%3D%26height%3D600"
    title="Entire session resume picker demo"
    loading="lazy"
    className="absolute top-0 left-0 h-full w-full border-0"
    allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture;"
    allowFullScreen
  />
</div>

- [`entire checkpoint explain`](https://docs.entire.io/cli/commands#explain) `--full` and `--verbose` now render external agent transcripts correctly, showing readable user, assistant, and tool-call output instead of a parse failure.

### Auth Status Reliability

- Auth status dates now stay stable across timezones, so date-only fields like `CREATED`, `LAST USED`, and `EXPIRES` do not change depending on where you run the command.
  - Thank you, [@stale2000](https://github.com/stale2000), for your contribution!

### Security and Reliability

- Checkpoint creation now preserves symlinks instead of following them, so a changed symlink cannot accidentally capture files from outside the repository.
  - Thank you, [@stale2000](https://github.com/stale2000), for your contribution!
- Path filtering now better distinguishes valid repository files from unsafe traversal paths, preventing legitimate generated files from being skipped while keeping checkpoint safety checks intact.
  - Thank you, [@stale2000](https://github.com/stale2000), for your contribution!
- Session recovery now keeps fallback transcript reads inside Entire's metadata directory, making recovery safer and more predictable when local paths or symlinks are unusual.
- The OpenAI Privacy Filter can now be enabled as an optional pre-push redaction pass, adding another privacy check before agent context leaves your machine without slowing down every commit.

### Testing Infrastructure

- Git test repositories now use the shared test setup, making CI less dependent on a developer's local Git identity, signing, or line-ending configuration.
  - Thank you, [@stale2000](https://github.com/stale2000), for your contribution!

## Entire Web

### Contributor and Agent Attribution

- Unknown agents now appear as their own segment in the top contributors chart, so AI-authored work without attribution is visible instead of disappearing from the breakdown.
- Each commit is now counted only once in the contributor agent breakdown, making the chart more accurate when a commit is associated with multiple sessions or attribution paths.

### Session and Filter Organization

- Session titles are now generated using Chicago-style title case, giving automatically named sessions a more consistent, readable shape across the app.
- Filter tabs are now saved per user in the database, so your custom views follow you across devices instead of living only in one browser.

### Navigation and Onboarding Polish

- The About Menu now includes a Documentation link, making setup and reference material easier to reach from inside the app.
- The homepage hero has a clearer header and subheader, so new visitors get a faster read on what Entire is for.
- New users now see a refreshed onboarding experience that makes it clearer how to get started and what to do before their first checkpoints appear.

![Refreshed Entire onboarding checklist without browser chrome](/blog/entire-dispatch-0x0013/onboarding-refresh.png)

## git-sync

_Released via [v0.7.0](https://github.com/entireio/git-sync/releases/tag/v0.7.0)._

### Git Transfer Reliability

- Initial sync now handles ref-only updates correctly by sending the empty pack Git servers expect, preventing strict servers from rejecting the push and leaving a mirror half-populated.
- Fetches from Git servers now fail clearly when the server omits the expected packfile, so `git-sync` no longer reports success after storing nothing.
- Large initial syncs can now recover from server timeouts by retrying in smaller batches, so long-running mirror setup is less likely to fail halfway through.

### Credential and SSH Safety

- Credential tokens are no longer printed in help text or error usage output, preventing accidental token leaks in CI logs.
- Credential lookup now includes the remote port, so remotes on non-default ports use the correct stored credentials.
- SSH destinations are now validated more strictly, preventing host or username values from being mistaken for SSH command options.

### Platform and Error Handling

- The token store now uses a Windows-compatible file lock, so `git-sync` can compile and manage credentials correctly on Windows.
- Busy repositories now handle push conflicts more gracefully: if the remote changes mid-sync, `git-sync` can retry instead of forcing you to restart the operation manually.
- Checkpoint parent-load failures now return a useful error instead of panicking on a nil commit.

That's the dispatch, rebels. Questions, bugs, or existential complaints about distributed systems belong in [Discord](https://discord.com/invite/jZJs3Tue4S) or [GitHub issues](https://github.com/entireio/cli/issues).

Boop.
